export type MentionToken = {
  id: string;
  label?: string;
};

// Token format: @user{<id>} or @user{<id>|<label>}
// Safe subset to avoid braces/pipe confusion in ids/labels.
const TOKEN_REGEX = /@user\{([^}|]+)(?:\|([^}]+))?\}/g;

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function parseMentionTokens(body: string): { plain: string; mentions: MentionToken[] } {
  if (!body) return { plain: '', mentions: [] };
  const mentions: MentionToken[] = [];
  let match: RegExpExecArray | null;
  TOKEN_REGEX.lastIndex = 0;
  while ((match = TOKEN_REGEX.exec(body)) !== null) {
    const [, id, label] = match;
    if (id) {
      mentions.push({ id, label: label?.trim() || undefined });
    }
  }
  return { plain: body, mentions };
}

export function formatMentionToken(mention: MentionToken): string {
  const safeId = (mention.id || '').trim();
  const safeLabel = mention.label?.trim();
  if (!safeId) return '';
  if (safeLabel) {
    return `@user{${safeId}|${safeLabel}}`;
  }
  return `@user{${safeId}}`;
}

export function replaceMentionPlaceholders(body: string, mentions: MentionToken[]): string {
  if (!body || mentions.length === 0) return body;
  // If already tokenized, leave as-is.
  if (/@user\{[^}]+\}/.test(body)) {
    return body;
  }
  let result = body;
  for (const mention of mentions) {
    const token = formatMentionToken(mention);
    if (!token) continue;
    result = result.replace(/@user\{[^}]*\}/, token);
  }
  return result;
}

export function extractMentionIds(body: string): string[] {
  return parseMentionTokens(body).mentions.map((m) => m.id);
}

export function renderTokensToDisplay(body: string, resolveLabel: (id: string, fallback?: string) => string | undefined): string {
  if (!body) return '';
  return body.replace(TOKEN_REGEX, (_match, id, label) => {
    const resolved = resolveLabel(id, label);
    const display = resolved || label || id;
    return `@${display}`;
  });
}

export function retokenizeFromMentions(
  body: string,
  mentions: string[],
  resolveLabel: (id: string) => string | undefined,
): string {
  if (!body || mentions.length === 0) return body;
  let result = body;
  for (const id of mentions) {
    const label = resolveLabel(id) || id;
    const token = formatMentionToken({ id, label });
    const pattern = new RegExp(`@${escapeRegex(label)}(\\b)?`);
    result = result.replace(pattern, token);
  }
  return result;
}
